import React, { FC, ChangeEvent } from 'react';

interface PdqTextareaProps {
  name: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  label?: string;
  invalidMessage?: string;
  wrapperDivClassName?: string;
  disabled?: boolean;
  required?: boolean;
  showValidation?: boolean;
  maxLength?: number;
  zIndex?: number;
  showCopyToClipboard?: boolean;
  rows?: number;
  minLength?: number;
  ignoreWhitespace?: boolean;
}

const TEXTAREA_PREFIX = 'pdq-textarea';

const PdqTextareaComponent: FC<PdqTextareaProps> = ({
  name,
  value,
  onChange,
  wrapperDivClassName = '',
  placeholder = '',
  label = undefined,
  invalidMessage = undefined,
  disabled = false,
  required = false,
  showValidation = false,
  maxLength = 255,
  zIndex = 1,
  showCopyToClipboard = false,
  rows = 5,
  minLength = 100,
  ignoreWhitespace = true,
}) => {
  // normalize value to remove whitespace if needed
  const normalizedValue = ignoreWhitespace ? value.replace(/\s/g, '') : value;

  const getClasses = () => {
    return [
      TEXTAREA_PREFIX,
      hasErrorMessage ? `${TEXTAREA_PREFIX}__invalid-border` : '',
      disabled ? `${TEXTAREA_PREFIX}__disabled` : '',
      !disabled && required && showValidation && !normalizedValue ? 'invalid' : '',
    ];
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(value);
  };

  const getCounterClass = () => {
    const baseClass = `${TEXTAREA_PREFIX}__counter`;
    if (required && showValidation && !normalizedValue) {
      return `${baseClass} ${baseClass}-required-message`;
    }
    if (required && showValidation && normalizedValue && normalizedValue.length < minLength) {
      return `${baseClass} ${baseClass}-invalid-message`;
    }
    return baseClass;
  };

  const hasErrorMessage = !disabled && required && showValidation && normalizedValue.length < minLength;

  return (
    <div className={wrapperDivClassName}>
      {label && (
        <label className={`${TEXTAREA_PREFIX}__label`} htmlFor={name}>
          {label}
          {required && <span>*</span>}
        </label>
      )}
      <div className={`${TEXTAREA_PREFIX}__wrap`}>
        <textarea
          name={name}
          rows={rows}
          className={getClasses().join(' ')}
          maxLength={maxLength}
          disabled={disabled}
          required={required}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
        />
        <div className={`${TEXTAREA_PREFIX}__actions`}>
          {showCopyToClipboard && (
            <img onClick={copyToClipboard} title="Copy to clipboard" src="assets/icons/copy-to-clipboard.svg" />
          )}
        </div>
        {maxLength && !disabled && (
          <div className={getCounterClass()}>
            {normalizedValue.length}/{maxLength}
          </div>
        )}
        {!disabled && required && showValidation && normalizedValue && normalizedValue.length < minLength && (
          <div className={`${TEXTAREA_PREFIX}__invalidMessage`}>{invalidMessage}</div>
        )}
        {!disabled && required && showValidation && !normalizedValue && (
          <label className="invalid-msg react-input__error-msg">*{label} is required</label>
        )}
      </div>
    </div>
  );
};

export default PdqTextareaComponent;
